/**
 * Phase 8 · Smart Match prompt search hook (24-Apr follow-up).
 *
 * Wraps `runPromptSearch` / `runMultiJdSearch` in a React Query `useQuery`
 * so the canvas re-fetches when the pipeline's prompt (or JD list) changes
 * and caches the result set for the life of the canvas session.
 *
 * Two modes:
 *   - Single prompt (default) — original behaviour: one embed → Pinecone
 *     → inference chain.
 *   - Multi-JD (N×M) — when `jds.length >= 1`, fan out one pipeline per
 *     JD and merge the results so the canvas can group by client / JD.
 *
 * The query is scoped by `(orgId, pipelineId, jdSignature)` so two
 * separate searches never clobber each other's cache entries and
 * navigating back to a historical thread re-uses the previous snapshot
 * instantly.
 */

import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { queryKeys } from '@core/query/queryKeys';
import {
  runPromptSearch,
  runMultiJdSearch,
  runBulkResumeSearch,
  runResumeVsOrgJobsSearch,
  type PromptSearchResult,
  type MultiJdInput,
  type MultiJdSearchResult,
  type BulkResumeInput,
} from '../lib/promptSearch';

interface Options {
  /** Only fire when the canvas has actually moved past clarify → stream. */
  enabled?: boolean;
  /** Top-K passed through to Pinecone (default 25). */
  topK?: number;
  /**
   * If provided, run the N×M multi-JD pipeline instead of the single-
   * prompt one. Each entry scores against the candidate pool independently
   * and is merged into a single flat result set tagged by `job_id`.
   */
  jds?: MultiJdInput[];
  /**
   * 21-Apr-26 — Bulk-resume mode. When the user uploads resume files
   * on §29.F we score them locally against the context JD (or a
   * paraphrase-derived synthetic JD). Resumes arrive with parsed text
   * via the `pendingResumeTexts` sessionStorage bridge; this mode is
   * mutually exclusive with `jds` (multi-JD takes priority).
   */
  bulkResumes?: BulkResumeInput[];
  /** Optional context JD body for bulk-resume scoring. */
  bulkContextJd?: string | null;
}

/** Empty fallback returned while disabled or before the query runs. */
const EMPTY: PromptSearchResult = {
  matches: [],
  source: 'empty',
  debug: {
    promptLength: 0,
    pineconeMatchCount: 0,
    hydratedCandidateCount: 0,
    scoredCount: 0,
  },
};

const EMPTY_MULTI: MultiJdSearchResult = {
  matches: [],
  jds: [],
  source: 'empty',
  debug: { jdCount: 0, totalScored: 0, skippedJds: 0 },
};

/**
 * Stable signature for the JD list so React Query can key the cache
 * without re-scoring on cosmetic re-renders. We concatenate id + a
 * short text hash so two threads with the same JD text share a row.
 */
function jdSignatureOf(jds: MultiJdInput[]): string {
  if (jds.length === 0) return 'none';
  return jds
    .map((j) => {
      const text = j.text ?? '';
      // Djb2-style hash — good enough for cache keying, collision-free
      // for realistic JD text lengths in a single session.
      let h = 5381;
      for (let i = 0; i < text.length; i++) {
        h = ((h << 5) + h + text.charCodeAt(i)) | 0;
      }
      return `${j.id}:${h}`;
    })
    .join('|');
}

export function useSmartMatchSearch(
  pipelineId: string,
  prompt: string,
  opts: Options = {},
): {
  data: PromptSearchResult;
  multi: MultiJdSearchResult;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const queryClient = useQueryClient();

  const jds = opts.jds ?? [];
  const bulkResumes = opts.bulkResumes ?? [];
  const useMulti = jds.length >= 1;
  const useBulk = !useMulti && bulkResumes.length > 0;
  const jdSignature = useMemo(() => jdSignatureOf(jds), [jds]);

  /** Short djb2 hash of the prompt so a different JD on the same pipelineId
   *  always triggers a fresh fetch rather than serving a stale cache entry. */
  const promptHash = useMemo(() => {
    const text = prompt.trim().slice(0, 600);
    if (!text) return 'empty';
    let h = 5381;
    for (let i = 0; i < text.length; i++) {
      h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    }
    return String(h >>> 0);
  }, [prompt]);
  const bulkSignature = useMemo(() => {
    if (bulkResumes.length === 0) return 'none';
    return bulkResumes
      .map((r) => {
        const text = r.text ?? '';
        let h = 5381;
        for (let i = 0; i < text.length; i++) {
          h = ((h << 5) + h + text.charCodeAt(i)) | 0;
        }
        return `${r.id}:${h}`;
      })
      .join('|');
  }, [bulkResumes]);

  const enabled =
    (opts.enabled ?? true) &&
    !!orgId &&
    !!pipelineId &&
    (useMulti || useBulk || prompt.trim().length >= 3);

  const single = useQuery({
    // Include promptHash so a different JD pasted into the same pipeline
    // session always runs a fresh search rather than returning a stale hit.
    queryKey: [...queryKeys.smartMatch.promptSearch(orgId, pipelineId), promptHash],
    queryFn: () => runPromptSearch(orgId, prompt, { topK: opts.topK }),
    enabled: enabled && !useMulti && !useBulk,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const multi = useQuery({
    queryKey: queryKeys.smartMatch.multiJdSearch(orgId, pipelineId, jdSignature),
    queryFn: () =>
      runMultiJdSearch(orgId, jds, {
        perJdTopK: opts.topK ?? 25,
        maxJds: 10,
      }),
    enabled: enabled && useMulti,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // 21-Apr-26 — Case B vs Case C routing for bulk-resume uploads.
  //
  //   Case B: resumes only (no context JD) → score each uploaded resume
  //           against real org jobs so the canvas can answer "which of
  //           our existing roles does this candidate fit?".
  //   Case C: resumes + context JD → score uploads against the provided
  //           JD (current `runBulkResumeSearch` behaviour).
  //
  // The `bulkContextJd` textarea is the signal. When it's non-empty we
  // keep the synthetic-JD path so the user's pasted brief is honoured;
  // when it's empty we pivot to the org-jobs pipeline which produces
  // real `client_id` / `job_id` rows that group-by-client/job works
  // against.
  const hasContextJd = !!(opts.bulkContextJd ?? '').trim();

  const bulk = useQuery({
    queryKey: [
      'smart-match',
      hasContextJd ? 'bulk-resume-search' : 'resume-vs-org-jobs',
      orgId,
      pipelineId,
      bulkSignature,
    ] as const,
    queryFn: async () => {
      if (hasContextJd) {
        return runBulkResumeSearch(orgId, bulkResumes, {
          contextJd: opts.bulkContextJd ?? null,
          paraphrase: prompt,
        });
      }
      // Case B: uploaded resumes vs real org jobs. If the org has no
      // active jobs at all (first-time setup) we gracefully fall back
      // to the synthetic-JD path so the user still sees their
      // uploads with some score instead of a blank canvas.
      const vsOrg = await runResumeVsOrgJobsSearch(orgId, bulkResumes);
      if (vsOrg.matches.length > 0) {
        return {
          matches: vsOrg.matches,
          jd: {
            id: '__org_jobs__',
            title: `${vsOrg.jobs.length} active jobs`,
            clientName: '',
          },
          source: 'bulk-local' as const,
          debug: {
            resumeCount: vsOrg.debug.resumeCount,
            scoredCount: vsOrg.matches.length,
            skippedCount: vsOrg.debug.pairSkipped,
            skillContextLoaded: true,
          },
        };
      }
      return runBulkResumeSearch(orgId, bulkResumes, {
        contextJd: null,
        paraphrase: prompt,
      });
    },
    enabled: enabled && useBulk,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  // After a single-prompt search resolves, the background
  // `crossScoreOrgCandidatesVsJobs` call (fired inside runPromptSearch /
  // fallbackPoolSearch) writes candidate × real-job rows to
  // `ggj_b2b_match_results`.  We give it ~3 seconds to finish, then
  // invalidate the alt-jobs cache so `useAlternateJobsForCandidates` picks
  // up the new rows and the "Also a fit for" card footer shows real data.
  useEffect(() => {
    if (!single.data?.matches?.length || !orgId) return;
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['smart-match', 'alt-jobs', orgId],
      });
    }, 3000);
    return () => clearTimeout(timer);
  }, [single.data, orgId, queryClient]);

  const singleData: PromptSearchResult = single.data ?? EMPTY;
  const multiData: MultiJdSearchResult = multi.data ?? EMPTY_MULTI;
  const bulkData = bulk.data;

  // Expose a unified `data` aligned with the `PromptSearchResult` shape
  // so banners + debug counters keep working without a branching
  // refactor. Multi fans out to `multiData.matches`; bulk uses the
  // locally-scored resume rows.
  const dataAlias: PromptSearchResult = useMulti
    ? {
        matches: multiData.matches,
        source:
          multiData.source === 'mixed' ? 'fallback-pool' : multiData.source,
        debug: {
          promptLength: prompt.length,
          pineconeMatchCount: multiData.matches.length,
          hydratedCandidateCount: multiData.matches.length,
          scoredCount: multiData.debug.totalScored,
        },
      }
    : useBulk && bulkData
      ? {
          matches: bulkData.matches,
          // `bulk-local` isn't a PromptSearchResult source variant; we
          // map it to `fallback-pool` so the UI treats it as "local
          // scoring, no Pinecone" — which is exactly what it is.
          source: bulkData.source === 'empty' ? 'empty' : 'fallback-pool',
          debug: {
            promptLength: prompt.length,
            pineconeMatchCount: 0,
            hydratedCandidateCount: bulkData.debug.resumeCount,
            scoredCount: bulkData.debug.scoredCount,
          },
        }
      : singleData;

  return {
    data: dataAlias,
    multi: multiData,
    isLoading: useMulti
      ? multi.isLoading
      : useBulk
        ? bulk.isLoading
        : single.isLoading,
    isFetching: useMulti
      ? multi.isFetching
      : useBulk
        ? bulk.isFetching
        : single.isFetching,
    isError: useMulti
      ? multi.isError
      : useBulk
        ? bulk.isError
        : single.isError,
    refetch: () => {
      void (useMulti
        ? multi.refetch()
        : useBulk
          ? bulk.refetch()
          : single.refetch());
    },
  };
}
